/**
 * Input sanitization utilities for security
 * Protects against XSS, injection attacks, and malicious inputs
 */

/**
 * Sanitize user input for text fields
 */
export function sanitizeText(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove HTML tags completely for text fields
  let sanitized = input.replace(/<[^>]*>/g, '');

  // Decode HTML entities to prevent double encoding
  sanitized = sanitized
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");

  // Re-encode for safety
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

  return sanitized.trim();
}

/**
 * Sanitize email input
 */
export function sanitizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  // Remove any HTML and script content, then decode entities BEFORE the
  // character whitelist — stripping an encoded apostrophe (&#x27;) would
  // otherwise leak a literal "x27" into the result.
  let sanitized = sanitizeText(email);
  sanitized = sanitized
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");

  // Email should only contain valid email characters
  sanitized = sanitized.replace(/[^a-zA-Z0-9@._+-]/g, '');

  return sanitized.toLowerCase().trim();
}

/**
 * Validate and sanitize input with rules
 */
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedValue: string;
}
